import type {
  Event,
  EventStatus,
  EventTask,
  EventTaskStatus,
  EventTeamMember,
  PaymentMethod,
  Rial,
  TaskChecklistItem,
} from "@kitchen/schemas";
import { ALLOWED_EVENT_TRANSITIONS } from "@kitchen/schemas";
import { Button, Chip, faDigits, Field, formatDate, formatMoney, formatTime, Panel, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, eventsApi, type StaffRow } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";
import { DaySheetPrintView } from "./DaySheetPrintView.js";

const fmtDate = (iso: string) => formatDate(iso.slice(0, 10));

const STATUS_LABELS: Record<
  EventStatus,
  { label: string; tone?: "ok" | "warn" | "coral" | "blue" | "dark" }
> = {
  draft: { label: "پیش‌نویس", tone: "dark" },
  confirmed: { label: "قطعی", tone: "blue" },
  preparation: { label: "آماده‌سازی", tone: "warn" },
  event_day: { label: "روز مراسم", tone: "ok" },
  settlement: { label: "تسویه", tone: "blue" },
  archived: { label: "بایگانی", tone: "dark" },
  cancelled: { label: "لغوشده", tone: "coral" },
};

const TRANSITION_LABELS: Record<EventStatus, string> = {
  draft: "پیش‌نویس",
  confirmed: "تأیید و قطعی‌سازی",
  preparation: "ورود به مرحله آماده‌سازی",
  event_day: "آغاز روز مراسم",
  settlement: "اتمام مراسم و آغاز تسویه",
  archived: "بایگانی نهایی",
  cancelled: "لغو مراسم",
};

interface EventDetailsModalProps {
  eventId: string;
  onClose: () => void;
}

type DetailsTab = "tasks" | "team" | "finance" | "info" | "daySheet";

export function EventDetailsModal({ eventId, onClose }: EventDetailsModalProps) {
  const [subTab, setSubTab] = useState<DetailsTab>("tasks");
  const refresh = [["events", eventId], ["events"]];

  const {
    data: event,
    isLoading,
    error,
  } = useQuery<Event & { team: EventTeamMember[]; tasks: EventTask[] }>({
    queryKey: ["events", eventId],
    queryFn: () => eventsApi.getEvent(eventId),
  });

  const staffQuery = useQuery({
    queryKey: ["staff"],
    queryFn: () => api<StaffRow[]>("/staff"),
  });

  const transitionAction = useAction(
    async ({ toStatus, reason }: { toStatus: EventStatus; reason?: string }) => {
      if (!event) return;
      await eventsApi.transitionEventStatus(event.id, event.version, {
        toStatus,
        reason: reason ?? "",
      });
    },
    refresh,
  );

  if (isLoading) {
    return (
      <Panel title="جزئیات مراسم">
        <p className="muted">در حال بارگذاری اطلاعات مراسم...</p>
      </Panel>
    );
  }

  if (error || !event) {
    return (
      <Panel title="جزئیات مراسم">
        <ErrorLine error={error instanceof Error ? error.message : "خطا در دریافت مراسم"} />
        <Button variant="ghost" onClick={onClose}>
          بازگشت
        </Button>
      </Panel>
    );
  }

  const allowedTransitions = ALLOWED_EVENT_TRANSITIONS[event.status];
  const progressPercent = Math.round(event.progressPerMille / 10);

  if (subTab === "daySheet") {
    return (
      <DaySheetPrintView
        eventId={eventId}
        onClose={() => {
          setSubTab("tasks");
        }}
      />
    );
  }

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Header bar */}
      <Panel>
        <div className="stack" style={{ gap: "var(--s3)" }}>
          <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="row" style={{ alignItems: "center", gap: "var(--s2)", marginBottom: "4px" }}>
                <h1 className="t-title" style={{ margin: 0, fontSize: "20px" }}>
                  {event.name}
                </h1>
                <Chip tone={STATUS_LABELS[event.status].tone}>{STATUS_LABELS[event.status].label}</Chip>
              </div>
              <div className="muted" style={{ fontSize: "13px" }}>
                مشتری: <strong>{event.customerName}</strong> ({faDigits(event.customerPhone)}) | محل:{" "}
                <strong>{event.place}</strong> | تعداد مهمان:{" "}
                <strong>{faDigits(event.guestCount)} نفر</strong>
              </div>
              <div className="muted" style={{ fontSize: "12px", marginTop: "2px" }}>
                زمان: {fmtDate(event.startAt)} از ساعت {formatTime(event.startAt)} تا{" "}
                {formatTime(event.endAt)} | آشپزخانه: {event.kitchenName}
              </div>
            </div>

            <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "center" }}>
              <Button
                variant="default"
                icon="printer"
                onClick={() => {
                  setSubTab("daySheet");
                }}
              >
                برگه روز مراسم (A4)
              </Button>
              <Button variant="ghost" onClick={onClose}>
                بستن
              </Button>
            </div>
          </div>

          {/* Progress Bar (§13.5: progressPerMille 0-1000) */}
          <div style={{ marginTop: "var(--s1)" }}>
            <div
              className="row"
              style={{ justifyContent: "space-between", fontSize: "12px", marginBottom: "4px" }}
            >
              <span style={{ fontWeight: 600 }}>پیشرفت اجرای مراسم:</span>
              <span
                style={{ fontWeight: 700, color: progressPercent === 100 ? "var(--ok-ink)" : "var(--ink)" }}
              >
                {faDigits(progressPercent)}٪ (
                {faDigits(event.tasks.filter((t) => t.status === "done").length)} از{" "}
                {faDigits(event.tasks.length)} کار)
              </span>
            </div>
            <div
              style={{
                height: "10px",
                background: "var(--line)",
                borderRadius: "5px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${String(progressPercent)}%`,
                  height: "100%",
                  background: progressPercent === 100 ? "var(--ok)" : "var(--blue-strong)",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>

          {/* Status transition action buttons */}
          {allowedTransitions.length > 0 && (
            <div
              className="row wrap"
              style={{
                gap: "var(--s2)",
                alignItems: "center",
                paddingTop: "var(--s2)",
                borderTop: "1px solid var(--card-line)",
              }}
            >
              <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted)" }}>تغییر وضعیت:</span>
              {allowedTransitions.map((nextStatus) => {
                const isCancel = nextStatus === "cancelled";
                return (
                  <Button
                    key={nextStatus}
                    variant={isCancel ? "urgent" : "primary"}
                    size="md"
                    disabled={transitionAction.busy}
                    onClick={() => {
                      let reason = "";
                      if (isCancel) {
                        const r = askReason(`لغو مراسم ${event.name}`);
                        if (!r) return;
                        reason = r;
                      }
                      transitionAction.run({ toStatus: nextStatus, reason });
                    }}
                  >
                    {TRANSITION_LABELS[nextStatus]}
                  </Button>
                );
              })}
            </div>
          )}

          <ErrorLine error={transitionAction.error} />
        </div>
      </Panel>

      {/* Navigation tabs */}
      <Seg<"tasks" | "team" | "finance" | "info">
        label="بخش‌های جزئیات مراسم"
        value={subTab}
        onChange={(v) => {
          setSubTab(v);
        }}
        options={[
          { value: "tasks", label: `کارهای مراسم (${faDigits(event.tasks.length)})` },
          { value: "team", label: `تیم اجرایی (${faDigits(event.team.length)})` },
          { value: "finance", label: "مالی و سود مراسم" },
          { value: "info", label: "مشخصات و منوی پذیرایی" },
        ]}
      />

      {/* Tab: Tasks */}
      {subTab === "tasks" && (
        <TasksSection event={event} staffList={staffQuery.data ?? []} refresh={refresh} />
      )}

      {/* Tab: Team */}
      {subTab === "team" && <TeamSection event={event} staffList={staffQuery.data ?? []} refresh={refresh} />}

      {/* Tab: Finance & Profit (§13.7) */}
      {subTab === "finance" && <FinanceAndProfitSection event={event} refresh={refresh} />}

      {/* Tab: Info & Menu */}
      {subTab === "info" && <InfoAndMenuSection event={event} />}
    </div>
  );
}

// ==========================================
// Tasks Section (§13.5)
// ==========================================
function TasksSection({
  event,
  staffList,
  refresh,
}: {
  event: Event & { team: EventTeamMember[]; tasks: EventTask[] };
  staffList: StaffRow[];
  refresh: string[][];
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newAssignedStaffId, setNewAssignedStaffId] = useState("");
  const [newDueAt, setNewDueAt] = useState(event.startAt.slice(0, 16));
  const [newChecklistText, setNewChecklistText] = useState("");
  const [newNote, setNewNote] = useState("");

  const addTaskAction = useAction(async () => {
    if (!newTitle.trim()) return;
    const staff = staffList.find((s) => s.id === newAssignedStaffId);
    const checklistItems: TaskChecklistItem[] = newChecklistText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((text, idx) => ({ id: String(idx + 1), text, done: false }));

    await eventsApi.createEventTask(event.id, {
      title: newTitle.trim(),
      assignedStaffId: newAssignedStaffId || null,
      assignedStaffName: staff?.displayName ?? null,
      dueAt: new Date(newDueAt).toISOString(),
      status: "todo",
      checklist: checklistItems,
      note: newNote.trim(),
    });

    setNewTitle("");
    setNewChecklistText("");
    setNewNote("");
    setShowAdd(false);
  }, refresh);

  const updateTaskAction = useAction(
    async ({
      taskId,
      version,
      patch,
    }: {
      taskId: string;
      version: number;
      patch: { status?: EventTaskStatus; checklist?: TaskChecklistItem[] };
    }) => {
      await eventsApi.updateEventTask(taskId, version, patch);
    },
    refresh,
  );

  return (
    <div className="stack" style={{ gap: "var(--s3)" }}>
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="t-title" style={{ margin: 0, fontSize: "16px" }}>
          لیست کارهای اجرایی و چک‌لیست‌ها
        </h3>
        <Button
          variant="primary"
          icon="plus"
          size="md"
          onClick={() => {
            setShowAdd(!showAdd);
          }}
        >
          {showAdd ? "انصراف" : "افزودن کار جدید"}
        </Button>
      </div>

      <ErrorLine error={addTaskAction.error || updateTaskAction.error} />

      {/* Add Task Form */}
      {showAdd && (
        <div
          className="card stack"
          style={{ gap: "var(--s3)", padding: "var(--s4)", background: "var(--panel)" }}
        >
          <h4 style={{ margin: 0, fontWeight: 700 }}>ثبت کار جدید برای این مراسم</h4>
          <div className="row wrap" style={{ gap: "var(--s2)" }}>
            <div style={{ flex: 2, minWidth: "200px" }}>
              <Field
                id="task-title"
                label="عنوان کار *"
                placeholder="مثلاً: تحویل میز و صندلی"
                value={newTitle}
                onChange={(e) => {
                  setNewTitle(e.target.value);
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: "160px" }}>
              <div className="field">
                <label htmlFor="task-staff">مسئول انجام</label>
                <select
                  id="task-staff"
                  className="input"
                  value={newAssignedStaffId}
                  onChange={(e) => {
                    setNewAssignedStaffId(e.target.value);
                  }}
                >
                  <option value="">(بدون مسئول / نامشخص)</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName} ({s.phone})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: "160px" }}>
              <Field
                id="task-due"
                label="زمان موعد"
                type="datetime-local"
                value={newDueAt}
                onChange={(e) => {
                  setNewDueAt(e.target.value);
                }}
              />
            </div>
          </div>

          <Field
            id="task-checklist"
            label="موارد چک‌لیست (هر مورد در یک سطر)"
            placeholder="بررسی ورودی&#10;چک کردن خنک‌کننده"
            value={newChecklistText}
            onChange={(e) => {
              setNewChecklistText(e.target.value);
            }}
          />

          <Field
            id="task-note"
            label="یادداشت و جزئیات تکمیلی"
            placeholder="توضیحات لازم برای مسئول اجرا..."
            value={newNote}
            onChange={(e) => {
              setNewNote(e.target.value);
            }}
          />

          <div className="row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
            <Button
              variant="ghost"
              onClick={() => {
                setShowAdd(false);
              }}
            >
              انصراف
            </Button>
            <Button
              variant="primary"
              disabled={!newTitle.trim() || addTaskAction.busy}
              onClick={() => {
                addTaskAction.run({});
              }}
            >
              افزودن کار
            </Button>
          </div>
        </div>
      )}

      {/* Task Cards */}
      {event.tasks.length === 0 ? (
        <Panel>
          <p className="muted">هنوز کاری برای این مراسم ثبت نشده است.</p>
        </Panel>
      ) : (
        event.tasks.map((task) => (
          <div
            key={task.id}
            className="card stack"
            style={{
              gap: "var(--s2)",
              padding: "var(--s3) var(--s4)",
              borderRight:
                task.status === "done"
                  ? "4px solid var(--ok)"
                  : task.status === "in_progress"
                    ? "4px solid var(--blue-strong)"
                    : "4px solid var(--warn)",
            }}
          >
            <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: "15px" }}>{task.title}</span>
                <span className="muted" style={{ fontSize: "12px", marginRight: "10px" }}>
                  موعد: {fmtDate(task.dueAt)} ساعت {formatTime(task.dueAt)}
                  {task.assignedStaffName && ` | مسئول: ${task.assignedStaffName}`}
                </span>
              </div>

              {/* Status toggles */}
              <div className="row" style={{ gap: "4px" }}>
                <Button
                  size="md"
                  variant={task.status === "todo" ? "default" : "ghost"}
                  onClick={() => {
                    updateTaskAction.run({
                      taskId: task.id,
                      version: task.version,
                      patch: { status: "todo" },
                    });
                  }}
                >
                  انجام‌نشده
                </Button>
                <Button
                  size="md"
                  variant={task.status === "in_progress" ? "primary" : "ghost"}
                  onClick={() => {
                    updateTaskAction.run({
                      taskId: task.id,
                      version: task.version,
                      patch: { status: "in_progress" },
                    });
                  }}
                >
                  در حال انجام
                </Button>
                <Button
                  size="md"
                  variant={task.status === "done" ? "primary" : "ghost"}
                  onClick={() => {
                    updateTaskAction.run({
                      taskId: task.id,
                      version: task.version,
                      patch: { status: "done" },
                    });
                  }}
                >
                  انجام شد
                </Button>
              </div>
            </div>

            {/* Checklist items (§13.5) */}
            {task.checklist.length > 0 && (
              <div
                style={{
                  background: "var(--panel)",
                  padding: "var(--s2) var(--s3)",
                  borderRadius: "var(--r-card)",
                  marginTop: "4px",
                }}
              >
                <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px" }}>چک‌لیست اقلام:</div>
                <div className="stack" style={{ gap: "4px" }}>
                  {task.checklist.map((item, idx) => (
                    <label
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={(e) => {
                          const updated = task.checklist.map((c, i) =>
                            i === idx ? { ...c, done: e.target.checked } : c,
                          );
                          // Auto set task done if all items ticked
                          const allDone = updated.every((c) => c.done);
                          updateTaskAction.run({
                            taskId: task.id,
                            version: task.version,
                            patch: {
                              checklist: updated,
                              ...(allDone && task.status !== "done" ? { status: "done" } : {}),
                            },
                          });
                        }}
                      />
                      <span
                        style={{
                          textDecoration: item.done ? "line-through" : "none",
                          color: item.done ? "var(--muted)" : "var(--ink)",
                        }}
                      >
                        {item.text}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {task.note && (
              <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "2px" }}>
                یادداشت: {task.note}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ==========================================
// Team Section (§13.4)
// ==========================================
function TeamSection({
  event,
  staffList,
  refresh,
}: {
  event: Event & { team: EventTeamMember[]; tasks: EventTask[] };
  staffList: StaffRow[];
  refresh: string[][];
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [staffId, setStaffId] = useState("");
  const [role, setRole] = useState("مسئول تشریفات");
  const [phone, setPhone] = useState("");
  const [note, setNewNote] = useState("");

  const addMemberAction = useAction(async () => {
    const s = staffList.find((item) => item.id === staffId);
    if (!s) return;
    await eventsApi.addEventTeamMember(event.id, {
      staffId: s.id,
      staffName: s.displayName,
      role: role.trim() || "عضو تیم",
      phone: phone.trim() || s.phone,
      note: note.trim(),
    });
    setStaffId("");
    setShowAdd(false);
  }, refresh);

  const removeMemberAction = useAction(async (memberId: string) => {
    await eventsApi.removeEventTeamMember(event.id, memberId);
  }, refresh);

  return (
    <div className="stack" style={{ gap: "var(--s3)" }}>
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="t-title" style={{ margin: 0, fontSize: "16px" }}>
          اعضای تیم اجرایی مراسم (پرسنل داخلی)
        </h3>
        <Button
          variant="primary"
          icon="plus"
          size="md"
          onClick={() => {
            setShowAdd(!showAdd);
          }}
        >
          {showAdd ? "انصراف" : "افزودن عضو تیم"}
        </Button>
      </div>

      <ErrorLine error={addMemberAction.error || removeMemberAction.error} />

      {/* Add Team Member Form */}
      {showAdd && (
        <div
          className="card stack"
          style={{ gap: "var(--s3)", padding: "var(--s4)", background: "var(--panel)" }}
        >
          <h4 style={{ margin: 0, fontWeight: 700 }}>افزودن عضو تیم برای این مراسم</h4>
          <div className="row wrap" style={{ gap: "var(--s2)" }}>
            <div style={{ flex: 1, minWidth: "180px" }}>
              <div className="field">
                <label htmlFor="team-staff">پرسنل *</label>
                <select
                  id="team-staff"
                  className="input"
                  value={staffId}
                  onChange={(e) => {
                    setStaffId(e.target.value);
                    const selected = staffList.find((s) => s.id === e.target.value);
                    if (selected && !phone) setPhone(selected.phone);
                  }}
                >
                  <option value="">(انتخاب پرسنل)</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName} ({s.phone})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: "160px" }}>
              <Field
                id="team-role"
                label="نقش در مراسم *"
                placeholder="مثلاً: مدیر سالن، سرآشپز اجرایی"
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: "150px" }}>
              <Field
                id="team-phone"
                label="شماره تماس"
                placeholder="0912..."
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                }}
              />
            </div>
          </div>

          <Field
            id="team-note"
            label="یادداشت / شرح وظایف"
            placeholder="توضیحات تکمیلی..."
            value={note}
            onChange={(e) => {
              setNewNote(e.target.value);
            }}
          />

          <div className="row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
            <Button
              variant="ghost"
              onClick={() => {
                setShowAdd(false);
              }}
            >
              انصراف
            </Button>
            <Button
              variant="primary"
              disabled={!staffId || !role.trim() || addMemberAction.busy}
              onClick={() => {
                addMemberAction.run({});
              }}
            >
              افزودن به تیم
            </Button>
          </div>
        </div>
      )}

      {/* Team Members List */}
      {event.team.length === 0 ? (
        <Panel>
          <p className="muted">هیچ عضو تیمی هنوز برای این مراسم ثبت نشده است.</p>
        </Panel>
      ) : (
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "var(--s3)" }}
        >
          {event.team.map((m) => (
            <div key={m.id} className="card stack" style={{ gap: "var(--s2)", padding: "var(--s3)" }}>
              <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "14px" }}>{m.staffName}</div>
                  <Chip tone="blue">{m.role}</Chip>
                </div>
                <Button
                  variant="ghost"
                  size="md"
                  icon="x"
                  disabled={removeMemberAction.busy}
                  onClick={() => {
                    removeMemberAction.run(m.id);
                  }}
                >
                  حذف
                </Button>
              </div>
              <div className="muted" style={{ fontSize: "13px", direction: "ltr", textAlign: "right" }}>
                {faDigits(m.phone)}
              </div>
              {m.note && <div style={{ fontSize: "12px", color: "var(--muted)" }}>{m.note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==========================================
// Info and Menu Section
// ==========================================
function InfoAndMenuSection({ event }: { event: Event & { team: EventTeamMember[]; tasks: EventTask[] } }) {
  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Event Details Grid */}
      <Panel title="مشخصات مالی و اجرایی مراسم">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "var(--s3)",
          }}
        >
          <div>
            <div className="muted" style={{ fontSize: "12px" }}>
              مشتری طرف قرارداد
            </div>
            <div style={{ fontWeight: 600 }}>{event.customerName}</div>
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>{faDigits(event.customerPhone)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: "12px" }}>
              محل و تاریخ برگزاری
            </div>
            <div style={{ fontWeight: 600 }}>{event.place}</div>
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>{fmtDate(event.startAt)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: "12px" }}>
              تعداد مهمانان
            </div>
            <div style={{ fontWeight: 600 }}>{faDigits(event.guestCount)} نفر</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: "12px" }}>
              آشپزخانه مسئول پخت
            </div>
            <div style={{ fontWeight: 600 }}>{event.kitchenName}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: "12px" }}>
              مبلغ بیعانه دریافتی
            </div>
            <div style={{ fontWeight: 700, color: "var(--ok-ink)" }}>{formatMoney(event.depositAmount)}</div>
          </div>
        </div>

        {event.notes && (
          <div
            style={{
              marginTop: "var(--s3)",
              borderTop: "1px solid var(--card-line)",
              paddingTop: "var(--s2)",
            }}
          >
            <div className="muted" style={{ fontSize: "12px" }}>
              توضیحات و هماهنگی‌های کلی:
            </div>
            <p style={{ margin: "4px 0 0 0", fontSize: "13px" }}>{event.notes}</p>
          </div>
        )}
      </Panel>

      {/* Menu lines */}
      <Panel title={`منوی غذای مراسم (${faDigits(event.menuLines.length)} قلم)`}>
        {event.menuLines.length === 0 ? (
          <p className="muted">قلم غذایی ثبت نشده است.</p>
        ) : (
          <div className="stack" style={{ gap: "var(--s2)" }}>
            {event.menuLines.map((m, idx) => (
              <div
                key={idx}
                className="row wrap"
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "var(--s2) var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div>
                  <strong>{m.foodName}</strong>
                </div>
                <div style={{ fontWeight: 700 }}>
                  {faDigits(m.portions)} پرس | {formatMoney(m.pricePerPortion)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Needs */}
      {event.needs.length > 0 && (
        <Panel title="ملزومات و امکانات مورد نیاز">
          <div className="row wrap" style={{ gap: "var(--s2)" }}>
            {event.needs.map((need, idx) => (
              <Chip key={idx} tone="blue">
                {need}
              </Chip>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

// ==========================================
// Finance & Profit Section (§13.7–§13.9)
// ==========================================
function FinanceAndProfitSection({
  event,
  refresh,
}: {
  event: Event & { team: EventTeamMember[]; tasks: EventTask[] };
  refresh: string[][];
}) {
  const [showAddCost, setShowAddCost] = useState(false);
  const [costCategory, setCostCategory] = useState<"rent" | "transport" | "other">("rent");
  const [costTitle, setCostTitle] = useState("");
  const [costAmountToman, setCostAmountToman] = useState("");
  const [costNote, setCostNote] = useState("");

  const [showGuestChange, setShowGuestChange] = useState(false);
  const [newGuestCount, setNewGuestCount] = useState(String(event.guestCount));
  const [guestChangeNote, setGuestChangeNote] = useState("");

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositMethod, setDepositMethod] = useState<PaymentMethod>("card");
  const [depositRef, setDepositRef] = useState("");

  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementMethod, setSettlementMethod] = useState<PaymentMethod>("card");
  const [settlementRef, setSettlementRef] = useState("");

  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problemTitle, setProblemTitle] = useState("");
  const [problemDesc, setProblemDesc] = useState("");

  const [showContainerModal, setShowContainerModal] = useState(false);
  const [containerQty, setContainerQty] = useState("10");
  const [containerNote, setContainerNote] = useState("");

  const profitQuery = useQuery({
    queryKey: ["events", event.id, "profit"],
    queryFn: () => eventsApi.getEventProfit(event.id),
  });

  const costLinesQuery = useQuery({
    queryKey: ["events", event.id, "cost-lines"],
    queryFn: () => eventsApi.listEventCostLines(event.id),
  });

  const changeRequestsQuery = useQuery({
    queryKey: ["events", event.id, "guest-changes"],
    queryFn: () => eventsApi.listEventGuestChangeRequests(event.id),
  });

  const problemsQuery = useQuery({
    queryKey: ["events", event.id, "problems"],
    queryFn: () => eventsApi.listEventProblems(event.id),
  });

  const addCostAction = useAction(async () => {
    const amountRial = (Number(costAmountToman.replace(/\D/g, "")) * 10) as Rial;
    if (!costTitle.trim() || amountRial <= 0) return;
    await eventsApi.addEventCostLine(event.id, {
      category: costCategory,
      title: costTitle.trim(),
      amountRial,
      note: costNote.trim(),
    });
    setCostTitle("");
    setCostAmountToman("");
    setCostNote("");
    setShowAddCost(false);
  }, refresh);

  const deleteCostAction = useAction(async ({ id, version }: { id: string; version: number }) => {
    await eventsApi.deleteEventCostLine(id, version);
  }, refresh);

  const requestGuestChangeAction = useAction(async () => {
    const count = Number(newGuestCount);
    if (count <= 0) return;
    await eventsApi.requestEventGuestChange(event.id, {
      newGuestCount: count,
      note: guestChangeNote.trim(),
    });
    setShowGuestChange(false);
    setGuestChangeNote("");
  }, refresh);

  const decideGuestChangeAction = useAction(
    async ({ requestId, decision }: { requestId: string; decision: "accept" | "reject" }) => {
      await eventsApi.decideEventGuestChange(requestId, {
        decision,
        note: decision === "accept" ? "تأیید شد" : "رد شد",
      });
    },
    refresh,
  );

  const createInvoiceAction = useAction(async () => {
    await eventsApi.createEventInvoice(event.id);
  }, refresh);

  const depositAction = useAction(async () => {
    await eventsApi.recordEventDeposit(event.id, {
      method: depositMethod,
      reference: depositRef.trim() ? depositRef.trim() : undefined,
    });
    setShowDepositModal(false);
    setDepositRef("");
  }, refresh);

  const settlementAction = useAction(async () => {
    await eventsApi.recordEventSettlement(event.id, {
      method: settlementMethod,
      reference: settlementRef.trim() ? settlementRef.trim() : undefined,
    });
    setShowSettlementModal(false);
    setSettlementRef("");
  }, refresh);

  const recordProblemAction = useAction(async () => {
    if (!problemTitle.trim()) return;
    await eventsApi.recordEventProblem(event.id, {
      title: problemTitle.trim(),
      description: problemDesc.trim(),
    });
    setProblemTitle("");
    setProblemDesc("");
    setShowProblemModal(false);
  }, refresh);

  const returnContainerAction = useAction(async () => {
    const qty = Number(containerQty);
    if (qty <= 0) return;
    await eventsApi.returnEventContainers(event.id, {
      containerTypeId: "01900000-0000-7000-8000-00000000c001",
      quantity: qty,
      note: containerNote.trim(),
    });
    setShowContainerModal(false);
    setContainerNote("");
  }, refresh);

  const profit = profitQuery.data;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* 1. Profit & Summary Card (§13.7) */}
      <Panel title="کارت سود و زیان مراسم (ریال دقیق)">
        {profitQuery.isLoading ? (
          <p className="muted">در حال محاسبه سود مراسم...</p>
        ) : profit ? (
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <div
              className="row wrap"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "var(--s3)",
              }}
            >
              <div
                style={{
                  padding: "var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div className="muted" style={{ fontSize: "12px" }}>
                  درآمد کل مراسم
                </div>
                <div style={{ fontSize: "18px", fontWeight: 700, marginTop: "4px" }}>
                  {formatMoney(profit.revenueRial)}
                </div>
              </div>

              <div
                style={{
                  padding: "var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div className="muted" style={{ fontSize: "12px" }}>
                  هزینه مواد اولیه (فرمول خام)
                </div>
                <div style={{ fontSize: "18px", fontWeight: 700, marginTop: "4px", color: "var(--ink)" }}>
                  {formatMoney(profit.materialsCostRial)}
                </div>
              </div>

              <div
                style={{
                  padding: "var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div className="muted" style={{ fontSize: "12px" }}>
                  دستمزد پرسنل موقت (HR)
                </div>
                <div style={{ fontSize: "18px", fontWeight: 700, marginTop: "4px" }}>
                  {formatMoney(profit.externalWagesRial)}
                </div>
              </div>

              <div
                style={{
                  padding: "var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div className="muted" style={{ fontSize: "12px" }}>
                  سایر هزینه‌ها (اجاره، حمل و ...)
                </div>
                <div style={{ fontSize: "18px", fontWeight: 700, marginTop: "4px" }}>
                  {formatMoney(profit.rentCostRial + profit.transportCostRial + profit.otherCostRial)}
                </div>
              </div>

              <div
                style={{
                  padding: "var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div className="muted" style={{ fontSize: "12px" }}>
                  جمع کل هزینه‌ها
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    marginTop: "4px",
                    color: "var(--coral-strong)",
                  }}
                >
                  {formatMoney(profit.totalCostRial)}
                </div>
              </div>

              <div
                style={{
                  padding: "var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: `2px solid ${profit.netProfitRial >= 0 ? "var(--ok)" : "var(--coral)"}`,
                }}
              >
                <div className="muted" style={{ fontSize: "12px" }}>
                  سود خالص مراسم
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 800,
                    marginTop: "4px",
                    color: profit.netProfitRial >= 0 ? "var(--ok-ink)" : "var(--coral-ink)",
                  }}
                >
                  {formatMoney(profit.netProfitRial)}
                </div>
                <div style={{ fontSize: "11px", marginTop: "2px" }} className="muted">
                  حاشیه سود: {faDigits((profit.marginPerMille / 10).toFixed(1))}٪
                </div>
              </div>
            </div>

            {/* Status indicators */}
            <div
              className="row wrap"
              style={{ gap: "var(--s2)", alignItems: "center", paddingTop: "var(--s2)" }}
            >
              {profit.orderId ? (
                <Chip tone="blue">سفارش هسته ایجاد شد</Chip>
              ) : (
                <Chip tone="dark">بدون سفارش هسته</Chip>
              )}

              {profit.invoiceId ? (
                <Chip tone="ok">فاکتور مالی صادر شده است</Chip>
              ) : (
                <Button
                  variant="default"
                  onClick={() => {
                    createInvoiceAction.run(undefined);
                  }}
                  disabled={createInvoiceAction.busy}
                >
                  صدور فاکتور رسمی
                </Button>
              )}

              <Button
                variant="ghost"
                onClick={() => {
                  setShowDepositModal(true);
                }}
              >
                ثبت پرداخت بیعانه ({formatMoney(profit.depositAmount)})
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowSettlementModal(true);
                }}
                disabled={profit.remainingSettlementRial <= 0}
              >
                تسویه حساب نهایی (مانده: {formatMoney(profit.remainingSettlementRial)})
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowGuestChange(true);
                }}
              >
                درخواست تغییر تعداد مهمان
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowContainerModal(true);
                }}
              >
                ثبت بازگشت ظروف امانی
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowProblemModal(true);
                }}
              >
                ثبت مشکل مراسم
              </Button>
            </div>
          </div>
        ) : (
          <p className="muted">اطلاعات مالی در دسترس نیست</p>
        )}
      </Panel>

      {/* 2. Pending Guest Change Requests (§13.8) */}
      {(changeRequestsQuery.data?.length ?? 0) > 0 && (
        <Panel title="درخواست‌های تغییر تعداد مهمان (§6.5)">
          <div className="stack" style={{ gap: "var(--s2)" }}>
            {changeRequestsQuery.data?.map((cr) => (
              <div
                key={cr.id}
                className="row wrap"
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "var(--s2) var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <div>
                  <strong>
                    تغییر مهمان: از {faDigits(cr.oldGuestCount)} به {faDigits(cr.newGuestCount)} نفر
                  </strong>
                  {cr.note && (
                    <div className="muted" style={{ fontSize: "12px" }}>
                      یادداشت: {cr.note}
                    </div>
                  )}
                  <div style={{ fontSize: "11px", marginTop: "2px" }}>
                    وضعیت:{" "}
                    {cr.status === "pending"
                      ? "در انتظار تأیید مدیر"
                      : cr.status === "accepted"
                        ? "تأیید شد"
                        : "رد شد"}
                  </div>
                </div>

                {cr.status === "pending" && (
                  <div className="row" style={{ gap: "var(--s2)" }}>
                    <Button
                      variant="default"
                      onClick={() => {
                        decideGuestChangeAction.run({ requestId: cr.id, decision: "accept" });
                      }}
                      disabled={decideGuestChangeAction.busy}
                    >
                      تأیید درخواست
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        decideGuestChangeAction.run({ requestId: cr.id, decision: "reject" });
                      }}
                      disabled={decideGuestChangeAction.busy}
                    >
                      رد درخواست
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* 3. Cost Lines (اجاره، حمل، سایر هزینه‌ها) */}
      <Panel
        title="هزینه‌های ثبت‌شده توسط مدیر (اجاره، حمل و نقل، سایر)"
        actions={
          <Button
            variant="ghost"
            onClick={() => {
              setShowAddCost((s) => !s);
            }}
          >
            {showAddCost ? "انصراف" : "افزودن سطر هزینه جدید"}
          </Button>
        }
      >
        <div className="stack" style={{ gap: "var(--s3)" }}>
          {showAddCost && (
            <div
              className="stack"
              style={{
                padding: "var(--s3)",
                background: "var(--panel)",
                borderRadius: "var(--r-card)",
                gap: "var(--s2)",
              }}
            >
              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <Field id="cost-cat" label="دسته‌بندی هزینه">
                  <select
                    id="cost-cat"
                    value={costCategory}
                    onChange={(e) => {
                      setCostCategory(e.target.value as "rent" | "transport" | "other");
                    }}
                  >
                    <option value="rent">اجاره سالن / تجهیزات</option>
                    <option value="transport">حمل و نقل و جابجایی</option>
                    <option value="other">سایر هزینه‌های متفرقه</option>
                  </select>
                </Field>
                <Field id="cost-title" label="شرح / عنوان هزینه">
                  <input
                    id="cost-title"
                    value={costTitle}
                    onChange={(e) => {
                      setCostTitle(e.target.value);
                    }}
                    placeholder="مثلاً: اجاره باند و استیج"
                  />
                </Field>
                <Field id="cost-amount" label="مبلغ هزینه (تومان)">
                  <input
                    id="cost-amount"
                    value={costAmountToman}
                    onChange={(e) => {
                      setCostAmountToman(e.target.value);
                    }}
                    placeholder="مثلاً: 3500000"
                  />
                </Field>
              </div>
              <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "flex-end" }}>
                <Field id="cost-note" label="توضیحات تکمیلی">
                  <input
                    id="cost-note"
                    value={costNote}
                    onChange={(e) => {
                      setCostNote(e.target.value);
                    }}
                    placeholder="یادداشت اختیاری..."
                  />
                </Field>
                <Button
                  variant="default"
                  onClick={() => {
                    addCostAction.run(undefined);
                  }}
                  disabled={addCostAction.busy}
                >
                  ثبت هزینه
                </Button>
              </div>
            </div>
          )}

          {(costLinesQuery.data?.length ?? 0) === 0 ? (
            <p className="muted">هیچ سطر هزینه‌ای برای این مراسم ثبت نشده است.</p>
          ) : (
            <div className="stack" style={{ gap: "var(--s2)" }}>
              {costLinesQuery.data?.map((cl) => (
                <div
                  key={cl.id}
                  className="row wrap"
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "var(--s2) var(--s3)",
                    background: "var(--card)",
                    borderRadius: "var(--r-card)",
                    border: "1px solid var(--card-line)",
                  }}
                >
                  <div>
                    <Chip tone="dark">
                      {cl.category === "rent" ? "اجاره" : cl.category === "transport" ? "حمل و نقل" : "سایر"}
                    </Chip>
                    <strong style={{ marginRight: "var(--s2)" }}>{cl.title}</strong>
                    {cl.note && (
                      <span className="muted" style={{ marginRight: "var(--s2)", fontSize: "12px" }}>
                        ({cl.note})
                      </span>
                    )}
                  </div>
                  <div className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
                    <span style={{ fontWeight: 700 }}>{formatMoney(cl.amountRial)}</span>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        deleteCostAction.run({ id: cl.id, version: cl.version });
                      }}
                    >
                      حذف
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Panel>

      {/* 4. Problems Recorded (§13.9) */}
      {(problemsQuery.data?.length ?? 0) > 0 && (
        <Panel title="مشکلات ثبت‌شده پس از مراسم (§13.9)">
          <div className="stack" style={{ gap: "var(--s2)" }}>
            {problemsQuery.data?.map((p) => (
              <div
                key={p.id}
                style={{
                  padding: "var(--s2) var(--s3)",
                  background: "var(--card)",
                  borderRadius: "var(--r-card)",
                  border: "1px solid var(--card-line)",
                }}
              >
                <strong>{p.title}</strong>
                {p.description && <p style={{ marginTop: "4px", fontSize: "13px" }}>{p.description}</p>}
                <div className="muted" style={{ fontSize: "11px", marginTop: "4px" }}>
                  ثبت‌شده توسط {p.recordedByStaffName} در {fmtDate(p.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Modals */}
      {showGuestChange && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg)",
              padding: "var(--s4)",
              borderRadius: "var(--r-card)",
              minWidth: "320px",
            }}
          >
            <h3>درخواست تغییر تعداد مهمان مراسم</h3>
            <Field id="change-guest-cnt" label="تعداد جدید مهمان">
              <input
                id="change-guest-cnt"
                value={newGuestCount}
                onChange={(e) => {
                  setNewGuestCount(e.target.value);
                }}
                type="number"
              />
            </Field>
            <Field id="change-guest-note" label="علت تغییر">
              <input
                id="change-guest-note"
                value={guestChangeNote}
                onChange={(e) => {
                  setGuestChangeNote(e.target.value);
                }}
                placeholder="مثلاً: افزایش میهمانان کارفرما"
              />
            </Field>
            <div
              className="row"
              style={{ gap: "var(--s2)", marginTop: "var(--s3)", justifyContent: "flex-end" }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  setShowGuestChange(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  requestGuestChangeAction.run(undefined);
                }}
                disabled={requestGuestChangeAction.busy}
              >
                ارسال درخواست
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDepositModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg)",
              padding: "var(--s4)",
              borderRadius: "var(--r-card)",
              minWidth: "320px",
            }}
          >
            <h3>ثبت پرداخت بیعانه مراسم</h3>
            <p className="muted">مبلغ بیعانه: {formatMoney(profit?.depositAmount ?? 0)}</p>
            <Field id="dep-method" label="روش پرداخت">
              <select
                id="dep-method"
                value={depositMethod}
                onChange={(e) => {
                  setDepositMethod(e.target.value as PaymentMethod);
                }}
              >
                <option value="card">کارت به کارت / شتاب</option>
                <option value="bank-transfer">واریز به حساب / پایا</option>
                <option value="pos">کارتخوان (POS)</option>
                <option value="cash">نقدی</option>
                <option value="cheque">چک</option>
              </select>
            </Field>
            <Field id="dep-ref" label="شماره پیگیری / مرجع">
              <input
                id="dep-ref"
                value={depositRef}
                onChange={(e) => {
                  setDepositRef(e.target.value);
                }}
                placeholder="شماره ارجاع تراکنش..."
              />
            </Field>
            <div
              className="row"
              style={{ gap: "var(--s2)", marginTop: "var(--s3)", justifyContent: "flex-end" }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDepositModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  depositAction.run(undefined);
                }}
                disabled={depositAction.busy}
              >
                ثبت پرداخت
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSettlementModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg)",
              padding: "var(--s4)",
              borderRadius: "var(--r-card)",
              minWidth: "320px",
            }}
          >
            <h3>ثبت تسویه حساب نهایی مراسم</h3>
            <p className="muted">مانده قابل پرداخت: {formatMoney(profit?.remainingSettlementRial ?? 0)}</p>
            <Field id="settle-method" label="روش پرداخت">
              <select
                id="settle-method"
                value={settlementMethod}
                onChange={(e) => {
                  setSettlementMethod(e.target.value as PaymentMethod);
                }}
              >
                <option value="card">کارت به کارت / شتاب</option>
                <option value="bank-transfer">واریز به حساب / پایا</option>
                <option value="pos">کارتخوان (POS)</option>
                <option value="cash">نقدی</option>
                <option value="cheque">چک</option>
              </select>
            </Field>
            <Field id="settle-ref" label="شماره پیگیری / مرجع">
              <input
                id="settle-ref"
                value={settlementRef}
                onChange={(e) => {
                  setSettlementRef(e.target.value);
                }}
                placeholder="شماره ارجاع تراکنش..."
              />
            </Field>
            <div
              className="row"
              style={{ gap: "var(--s2)", marginTop: "var(--s3)", justifyContent: "flex-end" }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  setShowSettlementModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  settlementAction.run(undefined);
                }}
                disabled={settlementAction.busy}
              >
                تأیید و تسویه
              </Button>
            </div>
          </div>
        </div>
      )}

      {showProblemModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg)",
              padding: "var(--s4)",
              borderRadius: "var(--r-card)",
              minWidth: "320px",
            }}
          >
            <h3>ثبت مشکل مراسم (§13.9)</h3>
            <Field id="prob-title" label="عنوان مشکل">
              <input
                id="prob-title"
                value={problemTitle}
                onChange={(e) => {
                  setProblemTitle(e.target.value);
                }}
                placeholder="مثلاً: تأخیر در ارسال دسر"
              />
            </Field>
            <Field id="prob-desc" label="شرح و جزئیات">
              <textarea
                id="prob-desc"
                value={problemDesc}
                onChange={(e) => {
                  setProblemDesc(e.target.value);
                }}
                rows={3}
                placeholder="جزئیات و علت مشکل..."
              />
            </Field>
            <div
              className="row"
              style={{ gap: "var(--s2)", marginTop: "var(--s3)", justifyContent: "flex-end" }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  setShowProblemModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  recordProblemAction.run(undefined);
                }}
                disabled={recordProblemAction.busy}
              >
                ثبت مشکل
              </Button>
            </div>
          </div>
        </div>
      )}

      {showContainerModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg)",
              padding: "var(--s4)",
              borderRadius: "var(--r-card)",
              minWidth: "320px",
            }}
          >
            <h3>ثبت بازگشت ظروف امانی (§13.9)</h3>
            <Field id="cont-qty" label="تعداد ظروف برگشتی">
              <input
                id="cont-qty"
                value={containerQty}
                onChange={(e) => {
                  setContainerQty(e.target.value);
                }}
                type="number"
              />
            </Field>
            <Field id="cont-note" label="یادداشت">
              <input
                id="cont-note"
                value={containerNote}
                onChange={(e) => {
                  setContainerNote(e.target.value);
                }}
                placeholder="مثلاً: ۱۰ عدد بنماری استیل تحویل گرفته شد"
              />
            </Field>
            <div
              className="row"
              style={{ gap: "var(--s2)", marginTop: "var(--s3)", justifyContent: "flex-end" }}
            >
              <Button
                variant="ghost"
                onClick={() => {
                  setShowContainerModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  returnContainerAction.run(undefined);
                }}
                disabled={returnContainerAction.busy}
              >
                ثبت در کاردکس ظروف
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
